import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { telephonyApi } from './telephonyApi';

/** Small round icon button, quiet by default, magenta when active. */
const iconBtnStyle = (active: boolean): React.CSSProperties => ({
  width: 28,
  height: 28,
  borderRadius: 999,
  border: active ? 'none' : '1px solid #e4dcea',
  background: active ? '#E82C86' : '#fff',
  color: active ? '#fff' : '#6d6478',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  padding: 0,
  transition: 'background .15s, color .15s',
});

// Icon-font glyphs, not svg: several feature pages ship CSS that resizes
// or distorts svgs inside tables (see ClickToCall), and <i> glyphs with
// inline styles are immune.
const glyphStyle: React.CSSProperties = { fontSize: 11, lineHeight: 1 };
const PlayIcon = () => <i className="fa fa-play" aria-hidden style={glyphStyle} />;
const PauseIcon = () => <i className="fa fa-pause" aria-hidden style={glyphStyle} />;
const DownloadIcon = () => <i className="fa fa-download" aria-hidden style={{ ...glyphStyle, fontSize: 12 }} />;

/** Only one recording plays at a time across every table on the page. */
let activeAudio: HTMLAudioElement | null = null;

const fmt = (secs: number): string => {
  if (!isFinite(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Inline player for a stored call recording: play/pause, elapsed/total
 * time and a seekbar — compact enough for a table cell. The signed
 * playback URL is fetched once on first play.
 */
const RecordingPlayer: React.FC<{ recordingId: string }> = ({ recordingId }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'playing' | 'paused'>('idle');
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        if (activeAudio === audioRef.current) activeAudio = null;
      }
    };
  }, []);

  const toggle = async () => {
    if (status === 'playing') {
      audioRef.current?.pause();
      setStatus('paused');
      return;
    }
    if (audioRef.current) {
      if (activeAudio && activeAudio !== audioRef.current) activeAudio.pause();
      activeAudio = audioRef.current;
      await audioRef.current.play();
      setStatus('playing');
      return;
    }
    setStatus('loading');
    try {
      const r = await telephonyApi<{ url: string }>(
        `/v1/recordings/${recordingId}/playback`,
      );
      const audio = new Audio(r.url);
      audioRef.current = audio;
      audio.ontimeupdate = () => setTime(audio.currentTime);
      audio.ondurationchange = () => setDuration(audio.duration);
      audio.onended = () => setStatus('paused');
      audio.onpause = () => setStatus((s) => (s === 'loading' ? s : 'paused'));
      audio.onplay = () => setStatus('playing');
      if (activeAudio) activeAudio.pause();
      activeAudio = audio;
      await audio.play();
      setStatus('playing');
    } catch (err) {
      setStatus('idle');
      toast.error((err as Error).message);
    }
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = t;
      setTime(t);
    }
  };

  const started = status === 'playing' || status === 'paused';

  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: started ? 190 : 0 }}>
      <button
        type="button"
        title={status === 'playing' ? 'Pause' : 'Play recording'}
        aria-label={status === 'playing' ? 'Pause' : 'Play recording'}
        disabled={status === 'loading'}
        onClick={toggle}
        style={{ ...iconBtnStyle(status === 'playing'), opacity: status === 'loading' ? 0.5 : 1 }}
      >
        {status === 'playing' ? <PauseIcon /> : <PlayIcon />}
      </button>
      {started && (
        <>
          <input
            type="range"
            className="form-range"
            style={{ width: 90 }}
            min={0}
            max={duration || 0}
            step={0.1}
            value={time}
            onChange={seek}
          />
          <small className="text-muted text-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmt(time)} / {fmt(duration)}
          </small>
        </>
      )}
      <button
        type="button"
        title="Download recording"
        aria-label="Download recording"
        style={iconBtnStyle(false)}
        onClick={async () => {
          try {
            // ?download=1 presigns with an attachment disposition, so the
            // browser saves the file instead of playing it.
            const r = await telephonyApi<{ url: string }>(
              `/v1/recordings/${recordingId}/playback?download=1`,
            );
            window.location.assign(r.url);
          } catch (err) {
            toast.error((err as Error).message);
          }
        }}
      >
        <DownloadIcon />
      </button>
    </div>
  );
};

export default RecordingPlayer;
